import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNotification } from 'hooks'
import { useDelegateQuery } from '../../api'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Box, CircularProgress } from '@mui/material'
import style from '../../partners.modules.scss'
import ActionComponents from '../../components/ActionComponents'
import EditDelegateModal from '../EditDelegate/EditDelegateModal'
import { formatUIDateTime } from 'utils/helpers'
import ViewActionIcon from 'icons/ViewActionIcon'
import { Link } from 'components/core-ui/Link'
import { URLS } from 'components/iam/constants'
import { Table } from 'components/core-ui/Table'
import { getAssignmentsColumns, getDelegatesColumns } from './helpers'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { DeleteActionIcon } from 'icons'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'

const DelegateDetails: React.FC = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)

  const [shouldRefreshAssignmentsData, setShouldRefreshAssignmentsData] = useState<boolean>(false)
  const [shouldResetAssignmentsPage, setShouldResetAssignmentsPage] = useState<boolean>(false)
  const [assignmentsTableItems, setAssignmentsTableItems] = useState<any[]>([])
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState<boolean>(true)
  const [totalactiveAssignmentsCount, setTotalactiveAssignmentsCount] = useState<number>(0)

  const [shouldRefreshManagersData, setShouldRefreshManagersData] = useState<boolean>(false)
  const [shouldResetManagersPage, setShouldResetManagersPage] = useState<boolean>(false)
  const [managersTableItems, setManagersTableItems] = useState<any[]>([])
  const [isManagersLoading, setIsManagersLoading] = useState<boolean>(true)
  const [totalManagersCount, setTotalManagersCount] = useState<number>(0)

  const [currentAssignmentId, setCurrentAssignmentId] = useState<number | null>(null)
  const [isDeleteAssignmentModalOpen, setIsDeleteAssignmentModalOpen] = useState<boolean>(false)

  const openDeleteAssignmentModal = () => setIsDeleteAssignmentModalOpen(true)
  const closeDeleteAssignmentModal = () => {
    setIsDeleteAssignmentModalOpen(false)
  }

  const managersColumns = useMemo(() => getDelegatesColumns(), [])
  const assignmentsColumns = useMemo(() => getAssignmentsColumns(), [])

  const { canEdit } = { canEdit: true }

  const { id } = useParams<{ id: string }>()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const {
    data: delegate,
    isLoading,
    refetch
  } = useDelegateQuery(showNotification, Number(id), shouldRefreshData)

  const openEditModal = () => setIsEditModalOpen(true)
  const closeEditModal = () => setIsEditModalOpen(false)

  const fetchManagersData = async ({ page, rowsPerPage, order, orderBy, searchText }) => {
    setIsManagersLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/managers',
          tableFilters: [{ name: 'delegates.id', value: id }],
          searchText,
          searchFields: ['firstName', 'lastName', 'fullName', 'email'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setManagersTableItems(response.list || [])
      setTotalManagersCount(response.page.totalCount)
      setIsManagersLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching managers. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const fetchAssignmentsData = async ({ page, rowsPerPage, order, orderBy, searchText }) => {
    setIsAssignmentsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/assignments',
          tableFilters: [{ name: 'delegate.id', value: id }],
          searchText,
          searchFields: ['globalId', 'jobTitle', 'email', 'person.fullName'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setAssignmentsTableItems(response.list || [])
      setTotalactiveAssignmentsCount(response.page.totalCount)
      setIsAssignmentsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching assignments. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const assignmentDeleteAction = (assignmentId: number) => {
    setCurrentAssignmentId(assignmentId)
    openDeleteAssignmentModal()
  }

  if (isLoading)
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  if (!delegate) return <div>Delegate not found</div>

  return (
    <div className={style.mainContainer}>
      <div className={style.breadcrumbsContainer}>
        <Link href="/iam/partners" className={style.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <Link href={URLS.IAM_DELEGATES} className={style.breadcrumbsLink}>
          Delegates
        </Link>
        <span className={style.breadcrumbsSeparator}>|</span>
        <span className={style.breadcrumbsCurrent}>
          {delegate.firstName + ' ' + delegate.lastName}
        </span>
      </div>
      {canEdit && (
        <EditDelegateModal
          isModalOpen={isEditModalOpen}
          onModalClose={closeEditModal}
          delegateId={delegate.id}
          onSuccess={refetch}
        />
      )}
      <DeleteConfirmationModal
        isModalOpen={isDeleteAssignmentModalOpen}
        actionUrl="/iam/external-partners/assignments/delegate/:id"
        content="Are you sure you want to remove this assignment from delegate? This action cannot be undone."
        closeModal={closeDeleteAssignmentModal}
        itemId={currentAssignmentId}
        setShouldRefreshData={() => setShouldRefreshAssignmentsData(true)}
        succesMessage="Assignment deleted successfully from this delegate."
        title="Remove assignment"
        errorMessage="Failed to delete assignment from this delegate."
        method="PATCH"
      />
      <Box className={style.flexContainer} sx={{ justifyContent: 'stretch' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: delegate.firstName, label: 'First Name:' },
              { name: `${delegate.activeAssignmentsCount}`, label: 'Assignments:' },
              { name: delegate.lastName, label: 'Last Name:' },
              { name: delegate.phone, label: 'Phone:' },
              { name: delegate.email, label: 'Email:' },
              { name: delegate.isEnabled ? 'Enabled' : 'Disabled', label: 'Status:' }
            ]}
            title={'Delegate details'}
            actionComponents={
              <ActionComponents openDeleteModal={false} openEditModal={openEditModal} />
            }
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <InfoItems
            itemsPerRow={2}
            itemsSpacing={2}
            items={[
              { name: delegate.createdBy, label: 'Created By:' },
              { name: formatUIDateTime(delegate.createdDate), label: 'Created Date:' },
              { name: delegate.lastModifiedBy, label: 'Modified By:' },
              { name: formatUIDateTime(delegate.lastModifiedDate), label: 'Modified Date:' },
              { name: delegate.id.toString(), label: 'Delegate ID:' }
            ]}
            title={'Delegate metadata'}
          />
        </Box>
      </Box>
      <Box className={style.infoTableContainer}>
        <Box sx={{ flex: 1, marginTop: -5 }}>
          <Box sx={{ flex: 1 }}>
            <Table
              hideCheckbox
              hasSearchInput
              orderBy="person.firstName"
              order="asc"
              title="Assignments"
              columns={assignmentsColumns}
              items={assignmentsTableItems}
              fetchData={fetchAssignmentsData}
              isLoading={isAssignmentsLoading}
              totalCount={totalactiveAssignmentsCount}
              shouldRefreshData={shouldRefreshAssignmentsData}
              setShouldRefreshData={setShouldRefreshAssignmentsData}
              shouldResetPage={shouldResetAssignmentsPage}
              setShouldResetPage={setShouldResetAssignmentsPage}
              rowActions={[
                {
                  label: 'Delete',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <DeleteActionIcon />,
                  onClick: (_e, rowId) => assignmentDeleteAction(Number(rowId)),
                  backgroundColor: ''
                },
                {
                  label: 'Details',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <ViewActionIcon />,
                  onClick: (_e, rowId) => navigate(`/iam/externalPartners/assignments/${rowId}`),
                  backgroundColor: ''
                }
              ]}
              rowActionsWidth="80px"
              desktopRowActionsWidth="50px"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Table
              hideCheckbox
              hasSearchInput
              orderBy="firstName"
              order="asc"
              columns={managersColumns}
              items={managersTableItems}
              fetchData={fetchManagersData}
              isLoading={isManagersLoading}
              totalCount={totalManagersCount}
              shouldRefreshData={shouldRefreshManagersData}
              setShouldRefreshData={setShouldRefreshManagersData}
              shouldResetPage={shouldResetManagersPage}
              setShouldResetPage={setShouldResetManagersPage}
              rows={managersTableItems}
              title="Assigned by managers"
              rowActions={[
                {
                  label: 'Details',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  desktopIcon: <ViewActionIcon />,
                  onClick: (_e, rowId) => navigate(`/iam/externalPartners/managers/${rowId}`),
                  backgroundColor: ''
                }
              ]}
              rowActionsWidth="80px"
              desktopRowActionsWidth="50px"
            />
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default DelegateDetails
